/**
 * Lightweight Web Audio "ping" tone for admin support notifications.
 * No external assets, no library — generated on the fly.
 *
 * Usage:
 *   import { playSupportPing, isSupportSoundMuted, setSupportSoundMuted } from '../utils/supportPing';
 *   if (!isSupportSoundMuted()) playSupportPing();
 */

const STORAGE_KEY = 'admin_support_sound_muted';

let cachedCtx = null;

function getAudioContext() {
    if (typeof window === 'undefined') return null;
    if (cachedCtx) return cachedCtx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try {
        cachedCtx = new Ctx();
        return cachedCtx;
    } catch {
        return null;
    }
}

/** Two-tone "blip-blip" — pleasant, ~0.35s, ~-12 dBFS. */
export function playSupportPing() {
    const ctx = getAudioContext();
    if (!ctx) return;
    // Resume the context if it was suspended by autoplay policy
    if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch { /* ignore */ }
    }
    const now = ctx.currentTime;

    const tone = (start, freq, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.18, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur + 0.02);
    };

    tone(now,        880, 0.12);  // first blip (A5)
    tone(now + 0.13, 1320, 0.18); // second blip (E6)
}

export function isSupportSoundMuted() {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === '1';
}

export function setSupportSoundMuted(muted) {
    if (typeof localStorage === 'undefined') return;
    if (muted) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
}
